import { useEffect, useState } from 'react';

import { ArrowUpRight, ExternalLink, FileText, Mail, X } from 'lucide-react';

import { ScrollReveal } from '@/components/motion';
import type { NewsAttachment } from '@/data/news';

/** Shared uppercase brand-gold group subheading idiom. */
const GROUP_LABEL = 'font-label text-xs tracking-[0.22em] text-brand-gold uppercase';

/** Focus-visible ring shared by every interactive attachment element. */
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2';

/**
 * NewsAttachments (/news/:slug) — an article's attachments, grouped by kind.
 *
 * The four attachment types are partitioned with type-guard filters; each group
 * is rendered only when it carries items: embedded video/film frames, a gallery
 * of images (opening a lightweight lightbox), downloadable documents as plain
 * hover-lift cards, and a compact list of outbound links & resources.
 *
 * Motion: ScrollReveal staggers each group's items. Reduced-motion safe — the
 * lightbox uses only an optional opacity fade and is closable by backdrop, an X
 * button, or the Escape key. Every fact comes from '@/data/news'.
 */
export function NewsAttachments({ attachments }: { attachments: readonly NewsAttachment[] }) {
  const [active, setActive] = useState<number | null>(null);

  const images = attachments.filter(
    (a): a is Extract<NewsAttachment, { type: 'image' }> => a.type === 'image',
  );
  const videos = attachments.filter(
    (a): a is Extract<NewsAttachment, { type: 'video' }> => a.type === 'video',
  );
  const pdfs = attachments.filter(
    (a): a is Extract<NewsAttachment, { type: 'pdf' }> => a.type === 'pdf',
  );
  const links = attachments.filter(
    (a): a is Extract<NewsAttachment, { type: 'link' }> => a.type === 'link',
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActive(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  if (images.length === 0 && videos.length === 0 && pdfs.length === 0 && links.length === 0) {
    return null;
  }

  const activeImage = active !== null ? images[active] : undefined;

  return (
    <div className="space-y-10">
      {/* ── Video ──────────────────────────────────────────────────────── */}
      {videos.length > 0 && (
        <div className="space-y-4">
          <p className={GROUP_LABEL}>Video</p>
          <ScrollReveal stagger={0.08} className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {videos.map((video) => (
              <figure key={video.url} className="space-y-2">
                <div className="relative aspect-video overflow-hidden rounded-2xl border border-border bg-brand-navy">
                  {video.provider === 'youtube' ? (
                    <iframe
                      src={video.url}
                      title={video.label}
                      className="h-full w-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                    />
                  ) : (
                    <video controls preload="metadata" className="h-full w-full">
                      <source src={video.url} />
                    </video>
                  )}
                </div>
                <figcaption className="text-sm leading-relaxed text-muted-foreground">
                  {video.label}
                </figcaption>
              </figure>
            ))}
          </ScrollReveal>
        </div>
      )}

      {/* ── Gallery ────────────────────────────────────────────────────── */}
      {images.length > 0 && (
        <div className="space-y-4">
          <p className={GROUP_LABEL}>Gallery</p>
          <ScrollReveal
            stagger={0.05}
            className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
          >
            {images.map((image, index) => (
              <button
                key={image.url}
                type="button"
                onClick={() => setActive(index)}
                data-cursor
                className={`overflow-hidden rounded-xl transition-opacity duration-300 hover:opacity-90 motion-reduce:transition-none ${FOCUS_RING}`}
              >
                <img
                  src={image.url}
                  alt={image.label}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-xl object-cover"
                />
              </button>
            ))}
          </ScrollReveal>
        </div>
      )}

      {/* ── Documents ──────────────────────────────────────────────────── */}
      {pdfs.length > 0 && (
        <div className="space-y-4">
          <p className={GROUP_LABEL}>Documents</p>
          <ScrollReveal stagger={0.06} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {pdfs.map((pdf) => (
              <a
                key={pdf.url}
                href={pdf.url}
                target="_blank"
                rel="noreferrer"
                data-cursor
                className={`flex items-center gap-4 rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${FOCUS_RING}`}
              >
                <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                  <FileText aria-hidden className="size-6" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-display font-semibold text-foreground">
                    {pdf.label}
                  </span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    {pdf.size ?? 'PDF'}
                  </span>
                </span>
                <ArrowUpRight aria-hidden className="size-5 shrink-0 text-brand-gold" />
              </a>
            ))}
          </ScrollReveal>
        </div>
      )}

      {/* ── Links & resources ──────────────────────────────────────────── */}
      {links.length > 0 && (
        <div className="space-y-4">
          <p className={GROUP_LABEL}>Links &amp; resources</p>
          <ScrollReveal stagger={0.06} className="flex flex-col gap-3">
            {links.map((link) => {
              const isMail = link.url.startsWith('mailto:');
              const Icon = isMail ? Mail : ExternalLink;
              return (
                <a
                  key={link.url}
                  href={link.url}
                  target={isMail ? undefined : '_blank'}
                  rel="noreferrer"
                  data-cursor
                  className={`flex items-center gap-3 rounded-xl border border-border px-4 py-3 transition-colors hover:border-brand-gold/30 ${FOCUS_RING}`}
                >
                  <Icon aria-hidden className="size-4 shrink-0 text-brand-gold" />
                  <span className="text-sm font-medium text-brand-navy underline decoration-2 decoration-transparent underline-offset-4 transition-colors hover:decoration-brand-gold">
                    {link.label}
                  </span>
                </a>
              );
            })}
          </ScrollReveal>
        </div>
      )}

      {/* ── Lightbox ───────────────────────────────────────────────────── */}
      {activeImage && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-6 backdrop-blur-sm transition-opacity">
          {/* Full-bleed backdrop — a real button so close is keyboard-accessible. */}
          <button
            type="button"
            aria-label="Close"
            onClick={() => setActive(null)}
            className={`absolute inset-0 h-full w-full cursor-default ${FOCUS_RING}`}
          />
          <button
            type="button"
            aria-label="Close"
            onClick={() => setActive(null)}
            className={`absolute top-4 right-4 z-10 flex size-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 ${FOCUS_RING}`}
          >
            <X aria-hidden className="size-5" />
          </button>
          <figure className="relative z-10 flex flex-col items-center gap-3">
            <img
              src={activeImage.url}
              alt={activeImage.label}
              className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain"
            />
            <figcaption className="text-center text-sm text-white/80">
              {activeImage.label}
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
